import { z } from "zod";

/**
 * solar-battery — federal home-battery rebate sizer + indicative payback.
 *
 * Two calc surfaces over one DATE-keyed Clean Energy Regulator (CER) schedule:
 *
 *   1. `batteryRebate(installDate, input)` — the Cheaper Home Batteries
 *      subsidy delivered as Small-scale Technology Certificates (STCs) under
 *      the SRES. Rebate STCs = deeming-factor (STCs per usable kWh) × eligible
 *      capacity after the capacity taper, capped at the discounted usable-kWh
 *      ceiling. The indicative DOLLAR rebate = STCs × an assumed STC spot
 *      price (the price floats — it is an explicitly-flagged estimate, not a
 *      locked quote).
 *   2. `paybackEstimate(input)` — indicative simple-payback: (install cost −
 *      rebate) ÷ an annual-bill-saving RANGE (self-consumption + avoided peak
 *      ± feed-in-tariff change). Returned as a low/high RANGE with every
 *      assumption surfaced — never a precise "you'll save $X/yr" promise.
 *
 * SCHEME SHAPE (binding):
 *  - The Cheaper Home Batteries program started **1 July 2025**; the discount
 *    schedule was made tiered and stepped DOWN from **1 May 2026**. This module
 *    encodes the **post-1-May-2026** schedule (deeming-factor 6.8 STCs/usable
 *    kWh). Installs before 1 May 2026 valued against the earlier 8.4-STC
 *    schedule are out of this module's window — a new dated schedule file would
 *    encode them; we never mutate a shipped schedule.
 *  - The deeming-factor STEPS DOWN twice-yearly (Jan + Jul) toward the 2030
 *    SRES wind-down, so this ruleset is DATE-keyed (`rates-YYYY-MM-DD.ts`),
 *    NOT FY-keyed — re-load each January and July. See the schedule files.
 *
 * REGULATORY SHAPE (binding):
 *  - A home-battery install is goods + installation, NOT an s764A financial
 *    product — there is no AFSL perimeter. $0 affiliate is modelled here; the
 *    solar-installer affiliate handoff (a permitted, non-financial-product
 *    click-out) is kept OFF this calc surface entirely (facts only — no
 *    installer, no lead, no price-of-a-quote reaches an output).
 *  - Information, not advice: the rebate is "an indicative federal discount your
 *    installer should apply", framed as cited arithmetic, NEVER "buy a battery /
 *    you should size X kWh". The dollar figure is "indicative based on an STC
 *    price of $X", never a guaranteed saving. Every output cites CER / the
 *    scheme. See copy.ts.
 *
 * Pure functions only: inputs -> rules -> outputs. No I/O, no Date.now(); the
 * install date (which selects the schedule) is always passed in.
 */

/* ------------------------------------------------------------------------- */
/* Schedule metadata + rule data                                             */
/* ------------------------------------------------------------------------- */

/** Provenance for a dated battery-rebate schedule. Local to this module — the
 * shared `RuleSetMeta.product` is constrained to the AU0x suite; this is a
 * CER/SRES scheme, so it carries its own product tag. */
export interface BatteryScheduleMeta {
  /** Product tag for this (non-AU0x) ruleset. */
  product: "SOLAR_BATTERY";
  /** Registry key — the schedule's effective-from date (YYYY-MM-DD). */
  scheduleKey: string;
  /** Human label, e.g. "Cheaper Home Batteries schedule from 1 May 2026". */
  scheduleLabel: string;
  /** Inclusive start of the window this schedule governs (ISO date). */
  effectiveFrom: string;
  /** Inclusive end of the window, or `null` if open-ended (the live schedule).
   * The deeming factor steps twice-yearly, so a live schedule is `null` only
   * until the next Jan/Jul step is published, then it is closed and a new file
   * registered. */
  effectiveTo: string | null;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/**
 * One band of the capacity taper. The federal subsidy applies a declining
 * percentage as usable capacity grows: the first slab of usable kWh is
 * subsidised at 100%, the next at 60%, the next at 15%, and nothing above the
 * cap. Each band's `factor` multiplies the usable kWh that falls inside it.
 */
export interface CapacityTaperBand {
  /** Lower bound of usable kWh for this band, inclusive. */
  fromKwh: number;
  /** Upper bound of usable kWh, exclusive. `null` = the discounted cap ceiling. */
  toKwh: number | null;
  /** Subsidy fraction for usable kWh inside this band (e.g. 1.0, 0.6, 0.15). */
  factor: number;
}

export interface BatteryRebateSchedule extends BatteryScheduleMeta {
  /**
   * STC deeming factor: STCs created per ELIGIBLE usable kWh (after the taper).
   * Steps down twice-yearly toward 2030. 6.8 from 1 May 2026.
   */
  stcDeemingFactor: number;
  /** The capacity taper bands, authored to cover [0, discountedCapCapKwh). */
  taper: readonly CapacityTaperBand[];
  /** Minimum usable kWh for any rebate at all (program eligibility floor). */
  minUsableKwh: number;
  /** Maximum NOMINAL battery capacity (kWh) the program will accept. */
  maxNominalKwh: number;
  /** Usable-kWh ceiling: capacity above this earns no STCs (the taper cap). */
  discountedUsableCapKwh: number;
  /**
   * Indicative STC spot price (A$ per STC), net of the installer's transaction
   * /admin haircut. The STC price FLOATS (spot has traded near the $40 ex-GST
   * regulated clearing-house cap) — this is an explicitly-flagged ESTIMATE used
   * only to convert STC units into an indicative dollar figure. The STC *units*
   * are deterministic and CER-cited; the dollar conversion is not a quote.
   */
  indicativeStcPriceAud: number;
  /** The regulated STC clearing-house price cap (A$ ex-GST). CER-confirmed $40. */
  stcClearingHouseCapAud: number;
}

/* ------------------------------------------------------------------------- */
/* batteryRebate — inputs / outputs                                          */
/* ------------------------------------------------------------------------- */

export const BatteryRebateInputSchema = z.object({
  /**
   * Usable battery capacity in kWh (the figure the rebate is sized on). The
   * caller collects usable kWh; nominal capacity is checked separately for the
   * 100 kWh program ceiling.
   */
  usableKwh: z.number().positive(),
  /**
   * Nominal battery capacity in kWh, optional. When supplied it is checked
   * against the program's nominal ceiling (`maxNominalKwh`). When omitted we
   * assume nominal ≈ usable for the ceiling check.
   */
  nominalKwh: z.number().positive().optional(),
  /**
   * Whether the install meets the program's core non-capacity eligibility:
   * installed in a single job with new/existing rooftop solar PV, CEC-approved
   * product, on-grid VPP-capable, etc. The UI collects this as a single yes/no
   * gate; `false` (or omitted as `false`) ⇒ no rebate, with a reason. We do NOT
   * model each sub-condition — that is the installer's compliance step.
   */
  meetsCoreEligibility: z.boolean().default(true),
  /**
   * Override the schedule's indicative STC price (A$/STC). The price floats, so
   * the surface lets a caller pass a current estimate. Omitted ⇒ the schedule's
   * flagged default is used. Clamped to the clearing-house cap at calc time.
   */
  stcPriceOverrideAud: z.number().positive().optional(),
});

/** Caller-facing input: defaulted fields (e.g. `meetsCoreEligibility`) may be
 * omitted — the schema fills them at `.parse()`. Use `z.input` so the surface
 * doesn't force callers to pass values the schema defaults. */
export type BatteryRebateInput = z.input<typeof BatteryRebateInputSchema>;

export interface BatteryRebateOutput {
  /** Usable kWh the rebate was sized on (after the discounted-cap clamp). */
  eligibleUsableKwh: number;
  /** Usable kWh the user entered (pre-clamp), for "above the cap" framing. */
  enteredUsableKwh: number;
  /** STCs created for this battery (deeming factor × tapered eligible kWh). */
  stcCount: number;
  /** Per-band breakdown: usable kWh in each taper band and the STCs it earns. */
  taperBreakdown: readonly {
    fromKwh: number;
    toKwh: number | null;
    factor: number;
    kwhInBand: number;
    stcs: number;
  }[];
  /**
   * Indicative rebate in A$ = stcCount × the assumed STC price. INDICATIVE —
   * the STC price floats, so this is "≈ $X at an STC price of $price", not a
   * locked quote.
   */
  indicativeRebateAud: number;
  /** The STC price (A$/STC) the dollar figure was computed at. */
  stcPriceUsedAud: number;
  /** Whether any rebate is payable at all (eligibility + above the floor). */
  rebateAvailable: boolean;
  /** When no/partial rebate: a machine reason. `null` when fully available. */
  ineligibleReason:
    | "below-min-usable"
    | "over-nominal-cap"
    | "fails-core-eligibility"
    | null;
  /** True when the entered usable kWh exceeds the discounted cap (some kWh earn nothing). */
  cappedAtDiscountedCeiling: boolean;
  /** The schedule key (effective date) the rebate resolved against. */
  scheduleKey: string;
}

/* ------------------------------------------------------------------------- */
/* paybackEstimate — inputs / outputs                                        */
/* ------------------------------------------------------------------------- */

export const PaybackInputSchema = z.object({
  /** Total installed cost of the battery before the federal rebate (A$). */
  installCostAud: z.number().positive(),
  /**
   * The federal rebate to net off the install cost (A$). Pass the indicative
   * rebate from `batteryRebate` so the two surfaces compose; the payback then
   * carries the same "indicative" caveat the rebate does.
   */
  rebateAud: z.number().min(0),
  /**
   * Low and high estimates of the annual bill saving the battery delivers
   * (A$/yr) — typically self-consumption shifted off-peak + avoided peak import
   * ± any feed-in-tariff change. The CALLER supplies the range; this surface
   * does the payback arithmetic and surfaces it AS a range, never inventing a
   * single "you'll save $X" number.
   */
  annualSavingLowAud: z.number().min(0),
  annualSavingHighAud: z.number().min(0),
});

export type PaybackInput = z.infer<typeof PaybackInputSchema>;

export interface PaybackOutput {
  /** Net up-front cost after the federal rebate (A$). */
  netCostAud: number;
  /** The annual-saving range echoed back (A$/yr). */
  annualSavingLowAud: number;
  annualSavingHighAud: number;
  /**
   * Indicative simple-payback RANGE in years. `fastYears` uses the HIGH saving,
   * `slowYears` uses the LOW saving. Either is `null` when the corresponding
   * saving is 0 (never pays back at that assumption) — never Infinity.
   */
  fastYears: number | null;
  slowYears: number | null;
  /**
   * The assumptions baked into a simple-payback figure, surfaced so the output
   * is honest arithmetic, not a promise. Always present.
   */
  assumptions: readonly string[];
  /** True when even the optimistic saving never recovers the net cost. */
  neverPaysBack: boolean;
}
